<template>
  <b-modal
    v-model="showModal"
    size="xl"
    centered
    hide-footer
    no-close-on-backdrop
    modal-class="wizard-custom-document"
    @hidden="resetWizard"
  >
    <template #modal-header="{ close }">
      <wizard-header
        :current-step="currentStep"
        :total-steps="2"
        @close="cancel"
      />
    </template>

    <div class="wizard-body">
      <wizard-step1-select-type
        v-if="currentStep === 1"
        :document-types="documentTypes"
        :selected-type="selectedType"
        @select="selectDocumentType"
      />

      <wizard-step2-confirm
        v-if="currentStep === 2"
        :selected-type="selectedType"
        :document-types="documentTypes"
        :location="location"
      />
    </div>

    <wizard-footer
      :current-step="currentStep"
      :can-go-next="canGoNext"
      @next="nextStep"
      @previous="previousStep"
      @cancel="cancel"
      @create="createDocument"
    />
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import WizardHeader from './components/WizardHeader';
import WizardFooter from './components/WizardFooter';
import WizardStep1SelectType from './components/WizardStep1SelectType';
import WizardStep2Confirm from './components/WizardStep2Confirm';

import RepositoriesCustomTypeMixin from '@/febos/chile/commons/mixins/RepositoriesCustomTypeMixin';

export default {
  name: 'WizardDocumentosPersonalizados',
  components: {
    WizardHeader,
    WizardFooter,
    WizardStep1SelectType,
    WizardStep2Confirm,
  },
  mixins: [RepositoriesCustomTypeMixin],
  data() {
    return {
      currentStep: 1,
      selectedType: null,
      documentTypes: [],
      location: {
        type: 'folder',
        id: null,
        name: '',
        breadcrumb: []
      }
    };
  },
  computed: {
    ...mapGetters('Modals', ['modalName', 'modalData']),
    ...mapGetters('DocManagement', [
      'getSelectedRepositoryId',
      'getSelectedFolderId',
      'getTreeElements',
      'getRepositories'
    ]),
    ...mapGetters('Empresas', ['company']),

    showModal: {
      get() {
        const isOpen = this.modalName === 'wizardCustomDocument';
        console.log('🟢 Wizard: showModal getter', { modalName: this.modalName, isOpen });
        return isOpen;
      },
      set(value) {
        console.log('🟢 Wizard: showModal setter', value);
        if (!value) {
          this.closeModal();
        }
      }
    },

    canGoNext() {
      if (this.currentStep === 1) {
        return this.selectedType !== null;
      }
      return true;
    },

    stepTitle() {
      const titles = {
        1: 'Seleccionar Tipo de Documento',
        2: 'Confirmar y Crear'
      };
      return titles[this.currentStep] || '';
    }
  },
  watch: {
    showModal(newVal) {
      if (newVal) {
        this.initWizard();
      }
    }
  },
  methods: {
    ...mapActions('Modals', ['closeModal']),

    initWizard() {
      console.log('🟢 Wizard: initWizard called');
      console.log('🟢 Wizard: company', this.company);
      console.log('🟢 Wizard: optionsTypeDocument', this.optionsTypeDocument);

      // Cargar tipos de documentos según empresa
      this.documentTypes = this.optionsTypeDocument || [];
      console.log('🟢 Wizard: documentTypes loaded', this.documentTypes.length, 'types');

      // Configurar ubicación desde modalData o desde getters
      const locationId = this.modalData?.locationId
                        || this.getSelectedFolderId
                        || this.getSelectedRepositoryId;

      const locationType = this.modalData?.locationType
                          || (this.getSelectedFolderId ? 'folder' : 'root');

      console.log('🟢 Wizard: location', { locationId, locationType });

      this.location = {
        type: locationType,
        id: locationId,
        name: this.getLocationName(locationId, locationType),
        breadcrumb: this.buildBreadcrumb(locationId, locationType)
      };

      // Resetear selección
      this.selectedType = null;
      this.currentStep = 1;

      console.log('🟢 Wizard: initialization complete', {
        documentTypes: this.documentTypes.length,
        location: this.location,
        currentStep: this.currentStep
      });
    },

    getLocationName(locationId, locationType) {
      if (locationType === 'root') {
        const repo = this.getRepositories?.find((r) => r.id === locationId);
        return repo?.name || 'Repositorio';
      }

      const folder = this.findFolderInTree(locationId);
      return folder?.name || 'Carpeta';
    },

    buildBreadcrumb(locationId, locationType) {
      const breadcrumb = ['Repositorio'];

      if (locationType === 'folder' && locationId) {
        const folder = this.findFolderInTree(locationId);
        if (folder) {
          const path = this.getFolderPath(folder);
          breadcrumb.push(...path);
        }
      }

      return breadcrumb;
    },

    findFolderInTree(folderId, elements = null) {
      const tree = elements || this.getTreeElements || [];

      for (const element of tree) {
        if (element.id === folderId) {
          return element;
        }

        if (element.children && element.children.length > 0) {
          const found = this.findFolderInTree(folderId, element.children);
          if (found) return found;
        }
      }

      return null;
    },

    getFolderPath(folder, path = []) {
      if (!folder) return path;

      path.unshift(folder.name);

      if (folder.parentId) {
        const parent = this.findFolderInTree(folder.parentId);
        if (parent) {
          return this.getFolderPath(parent, path);
        }
      }

      return path;
    },

    selectDocumentType(type) {
      this.selectedType = type;
    },

    nextStep() {
      if (this.canGoNext && this.currentStep < 2) {
        this.currentStep++;
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    cancel() {
      this.closeModal();
    },

    createDocument() {
      if (!this.selectedType) return;

      this.$router.push({
        name: 'edit-custom-document',
        params: { type: this.selectedType },
        query: {
          locationType: this.location.type,
          locationId: this.location.id
        }
      });

      this.closeModal();
    },

    resetWizard() {
      this.currentStep = 1;
      this.selectedType = null;
      this.documentTypes = [];
      this.location = {
        type: 'folder',
        id: null,
        name: '',
        breadcrumb: []
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.wizard-body {
  min-height: 400px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 1.5rem;
}
</style>

<style lang="scss">
@import './wizard.scss';
</style>
